import { Injectable } from '@angular/core';
import {
  ConvertHelper,
  NotificationsService,
  PushAdapter,
  PushAdapterErrorType,
  PushAdapterSettings,
} from '@barthauer/abc/core';
import { CacheStorageAdapter, PersistenceService } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataEntity, MapActionIds } from '@barthauer/abc/graphics/adapters/open-layers';
import { ProfileService, ProfileSettingsService, UserService } from '@barthauer/basys/core/authentication';
import {
  ActionSetting,
  AdditionalObjectTypeConfig,
  BasysMapFrameInitConfig,
  BasysMapFrameService,
  BooleanSetting,
  CaptionStyle,
  RouteConfiguration,
  Setting,
  WidgetConfiguration,
} from '@barthauer/basys/map-frame';
import { CachePersistenceAdapter } from 'libs/abc/core/data/src/lib/caching/cache-persistence';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { MapInfoViewModel } from 'libs/basys/map-frame/src/lib/viewModels/mapsData.viewModel';
import { Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { WorkModeEnum } from '../models/mtData.model';
import { ResourcesComponent } from '../resources/resources/resources.component';
import { TicketState } from '../tickets/tickets.model';
import { TicketsComponent } from '../tickets/tickets/tickets.component';
import { MobileOrderViewModel } from '../viewModels/mobileData.viewModel';
import { WorkState, WorkViewMode } from '../works/works.model';
import { WorksComponent } from '../works/works/works.component';
import { DataService } from './data.service';
import { FrameService } from './frame.service';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

@Injectable()
export class AppService {
  public static readonly Route_OrderSelection: string = 'orderSelection';
  public static readonly Route_WorkModeSelection: string = 'workModeSelection';
  public static readonly Route_WorkList: string = 'WorkList';
  public static readonly Route_WorkDetail: string = 'WorkDetail';
  public static readonly Route_TaskDetail: string = 'TaskDetail';

  public static readonly Route_TicketList: string = 'TicketList';
  public static readonly Route_TicketDetail: string = 'TicketDetail';

  public static readonly Route_ResourceList: string = 'ResourceList';
  public static readonly Route_ResourceEventVehicleMileage: string = 'ResourceEventVehicleMileage';
  public static readonly Route_ResourceEventVehicleOperatingHours: string = 'ResourceEventVehicleOperatingHours';
  public static readonly Route_ResourceEventVehicleDowntime: string = 'ResourceEventVehicleDowntime';
  public static readonly Route_ResourceEventVehicleWaterFilling: string = 'ResourceEventVehicleWaterFilling';
  public static readonly Route_ResourceEventPersonnelWorkingTimes: string = 'ResourceEventPersonnelWorkingTimes';
  public static readonly Route_ResourceEventMaterialMaterialExpense: string = 'ResourceEventMaterialMaterialExpense';
  public static readonly Route_ResourceEventOtherOther: string = 'ResourceEventOtherOther';

  public static readonly Route_WorkDetail_ResourceList =
    AppService.Route_WorkDetail + '/' + AppService.Route_ResourceList;

  public static readonly RouterOutlet_Works: string = 'worksRouterOutlet';
  public static readonly RouterOutlet_Tickets: string = 'ticketsRouterOutlet';
  public static readonly RouterOutlet_Resources: string = 'resourcesRouterOutlet';

  public static readonly Widget_Works: string = 'Works';
  public static readonly Widget_Tickets: string = 'Tickets';
  public static readonly Widget_Resources: string = 'Resources';

  public static readonly Setting_Order: string = 'Order';
  public static readonly Setting_ShowWorkDetails: string = 'ShowWorkDetails';
  public static readonly Setting_AggregatePathWorks: string = 'AggregatePathWorks';
  public static readonly Setting_SaveChangesImmediately: string = 'SaveChangesImmediately';
  public static readonly Setting_EnablePushNotifications: string = 'EnablePushNotifications';
  public static readonly Setting_SaveChangesNow: string = 'SaveChangesNow';
  public static readonly Setting_AppOnline: string = 'AppOnline';
  public static readonly Setting_ExportWorkData: string = 'ExportWorkData';
  public static readonly Setting_ExportOfflineWorkData: string = 'ExportOfflineWorkData';

  public static readonly ContextMenuCommandName_Opera_ShowWorksList: string = 'Opera_ShowWorksList';
  public static readonly ContextMenuCommandName_Opera_ShowSequenceWorksList: string = 'Opera_ShowSequenceWorksList';
  public static readonly ContextMenuCommandName_Opera_NewWork: string = 'Opera_NewWork';
  public static readonly ContextMenuCommandName_Opera_ShowTicketsList: string = 'Opera_ShowTicketsList';
  public static readonly ContextMenuCommandName_Opera_NewTicket: string = 'Opera_NewTicket';
  public static readonly WORKMODE_TICKETONLY_ID = 'Workmode_TicketOnly_ID';
  public resAsync: Promise<ResourceSet>;

  componentRef: any;
  private order: MobileOrderViewModel;
  private subscriptions: Subscription[] = [];
  private loadOrderSubject = new Subject<MobileOrderViewModel>();
  private displayLoaderSubject = new Subject<boolean>();
  public displayLoader$ = this.displayLoaderSubject.asObservable();
  public queryMobileOrdersForSelection$: Observable<MobileOrderViewModel[]>;
  private orders: MobileOrderViewModel[];
  private workMode: WorkModeEnum = WorkModeEnum.Default;
  private pushAdapter: PushAdapter;

  constructor(
    public dataService: DataService,
    private frameService: FrameService,
    private basysMapFrameService: BasysMapFrameService,
    private persistenceService: PersistenceService,
    private resourceService: ResourceManagerService,
    private profileService: ProfileService,
    private profileSettingsService: ProfileSettingsService,
    private notificationsService: NotificationsService,
    private messageBoxService: MessageBoxService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-maintenance-opera');

    this.armMobileOrdersQuery();
    this.dataService.getOrderId = () => this.getLastOrderIdAsync();

    this.loadOrderSubject
      .asObservable()
      .pipe(
        tap(async (order) => {
          this.order = order;
          await this.refreshData();
        }),
      )
      .subscribe();

    this.handleModifyConfig();
    this.handleMapInfoLoaded();
    this.handleWidgetInstantiated();
    this.handleMapActions();
  }

  private async refreshData(closeBottomSheetAndRepublish?: boolean): Promise<void> {
    if (this.order) {
      this.frameService.refreshMobileWorks(undefined, undefined, closeBottomSheetAndRepublish);

      if (await this.dataService.hasMtResources()) {
        this.frameService.refreshMobileResourceTypes();
      }

      if (await this.dataService.hasMtWorkTypes()) {
        this.frameService.refreshMobileWorkTypes();
      }
    }

    //if there are tickets, they'll be loaded with the order. in workmode ticket there is another call: loadTicketsAsync
    const isTicketCommandAvailable = await this.ticketCommandsAvailable();
    this.dataService.hasMtTickets().subscribe((hasTickets) => {
      if (hasTickets && isTicketCommandAvailable) {
        this.frameService.refreshMobileTickets(undefined, closeBottomSheetAndRepublish);
      }
    });
  }

  private handleModifyConfig(): void {
    this.basysMapFrameService.modifyConfig = async (config) => {
      const idListParam: ParameterValue = new ParameterValue('IdList', this.order?.id);
      const auftragsIdParam: ParameterValue = new ParameterValue('AuftragsId', this.order?.id);

      config.mapConfig.ReadMapMessage.Parameters = [idListParam, auftragsIdParam];
      config.mapConfig.CacheFilterExpression =
        'Exists(Type(MtObject)[GeObjectId = ^.Id].Works[AuftragsId = $AuftragsId])';

      //works
      const worklistRoute = this.basysMapFrameService.createRouteConfig(
        AppService.Route_WorkList,
        AppService.Route_WorkList,
        AppService.RouterOutlet_Works,
      );

      if (this.workMode != WorkModeEnum.Ticket) {
        config.widgets.splice(
          0,
          0,
          this.basysMapFrameService.createWidgetConfig(
            AppService.Widget_Works,
            WorksComponent,
            (await this.resAsync).svgName('Works-Image'),
            undefined,
            () => {
              const incompleteWorksCount: number = this.getIncompleteWorksCount();
              return incompleteWorksCount > 0
                ? incompleteWorksCount > 99
                  ? '99+'
                  : incompleteWorksCount.toString()
                : null;
            },
            worklistRoute,
          ),
        );
      }

      config.routes.push(worklistRoute);
      config.routes.push(this.frameService.orderSelectionRoute);
      config.routes.push(
        this.basysMapFrameService.createRouteConfig(
          AppService.Route_WorkDetail,
          AppService.Route_WorkDetail,
          AppService.RouterOutlet_Works,
        ),
      );
      config.routes.push(
        this.basysMapFrameService.createRouteConfig(
          AppService.Route_TaskDetail,
          AppService.Route_TaskDetail,
          AppService.RouterOutlet_Works,
        ),
      );

      //tickets
      const ticketlistRoute = this.basysMapFrameService.createRouteConfig(
        AppService.Route_TicketList,
        AppService.Route_TicketList,
        AppService.RouterOutlet_Tickets,
      );

      const isTicketLicensed: boolean = await this.dataService.isTicketLicensed();
      const isTicketCommandSelected: boolean = await this.ticketCommandsAvailable();
      const shouldShowTicketWidget = isTicketLicensed && isTicketCommandSelected;

      let ticketPos: number = 0;
      if (shouldShowTicketWidget && this.workMode != WorkModeEnum.Order) {
        ticketPos = this.workMode === WorkModeEnum.Default ? 1 : 0;
        config.widgets.splice(
          ticketPos,
          0,
          this.basysMapFrameService.createWidgetConfig(
            AppService.Widget_Tickets,
            TicketsComponent,
            (await this.resAsync).svgName('Tickets-Image'),
            undefined,
            () => {
              const incompleteTicketsCount: number = this.getIncompleteTicketsCount();
              return incompleteTicketsCount > 0
                ? incompleteTicketsCount > 99
                  ? '99+'
                  : incompleteTicketsCount.toString()
                : null;
            },
            ticketlistRoute,
          ),
        );
      }

      config.routes.push(ticketlistRoute);
      config.routes.push(
        this.basysMapFrameService.createRouteConfig(
          AppService.Route_TicketDetail,
          AppService.Route_TicketDetail,
          AppService.RouterOutlet_Tickets,
        ),
      );

      //resources
      const resourceslistRoute = this.basysMapFrameService.createRouteConfig(
        AppService.Route_ResourceList,
        AppService.Route_ResourceList,
        AppService.RouterOutlet_Resources,
      );

      if (await this.dataService.hasMtResources()) {
        config.widgets.splice(
          ++ticketPos,
          0,
          this.basysMapFrameService.createWidgetConfig(
            AppService.Widget_Resources,
            ResourcesComponent,
            (await this.resAsync).svgName('Resources-Image'),
            undefined,
            undefined,
            resourceslistRoute,
          ),
        );

        const resourceRoutes: RouteConfiguration[] = new Array<RouteConfiguration>();

        resourceRoutes.push(resourceslistRoute);
        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventVehicleMileage,
            AppService.Route_ResourceEventVehicleMileage,
            AppService.RouterOutlet_Resources,
          ),
        );

        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventVehicleOperatingHours,
            AppService.Route_ResourceEventVehicleOperatingHours,
            AppService.RouterOutlet_Resources,
          ),
        );
        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventVehicleDowntime,
            AppService.Route_ResourceEventVehicleDowntime,
            AppService.RouterOutlet_Resources,
          ),
        );
        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventVehicleWaterFilling,
            AppService.Route_ResourceEventVehicleWaterFilling,
            AppService.RouterOutlet_Resources,
          ),
        );
        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventPersonnelWorkingTimes,
            AppService.Route_ResourceEventPersonnelWorkingTimes,
            AppService.RouterOutlet_Resources,
          ),
        );
        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventMaterialMaterialExpense,
            AppService.Route_ResourceEventMaterialMaterialExpense,
            AppService.RouterOutlet_Resources,
          ),
        );
        resourceRoutes.push(
          this.basysMapFrameService.createRouteConfig(
            AppService.Route_ResourceEventOtherOther,
            AppService.Route_ResourceEventOtherOther,
            AppService.RouterOutlet_Resources,
          ),
        );

        //create same routes for works
        resourceRoutes
          .slice()
          .forEach((rr) =>
            resourceRoutes.push(
              this.basysMapFrameService.createRouteConfig(
                AppService.Route_WorkDetail + '/' + rr.designation,
                AppService.Route_WorkDetail + '/' + rr.path,
                AppService.RouterOutlet_Works,
              ),
            ),
          );

        resourceRoutes.forEach((rr) => config.routes.push(rr));
      }

      let considerOfflineMode =
        this.basysMapFrameService.config.isAppLike !== false || CacheStorageAdapter.isDownloadComplete;
      const newsettings = new Map<string, Setting>();
      for (const s of Array.from(config.settings)) {
        const k = s[0];
        const v = s[1];
        newsettings.set(k, v);
        if (k === BasysMapFrameService.Setting_CacheConfig) {
          const saveAction: (isManual: boolean) => Promise<void> = async (isManual) =>
            this.frameService.saveOfflineData(isManual);
          const refreshAction: () => Promise<void> = async () => {
            await this.refreshData(true);
          };

          if (considerOfflineMode) {
            v.beginGroupBehind = false;
            const newswitchsetting = new BooleanSetting({
              caption: (await this.resAsync).get('AppOnline'),
              sortOrder: 1,
              enabled: CacheStorageAdapter.isOnline && CacheStorageAdapter.isDownloadComplete,
              value: !CacheStorageAdapter.forceOfflineMode,
              valueChanged: (value) => {
                if (CacheStorageAdapter.forceOfflineMode !== !value) {
                  CacheStorageAdapter.forceOfflineMode = !value;
                  if (value) {
                    saveAction(false);
                  }
                }
              },
            });
            newsettings.set(AppService.Setting_AppOnline, newswitchsetting);

            CacheStorageAdapter.isDataChanged.subscribe(() => {
              newswitchsetting.value = !CacheStorageAdapter.forceOfflineMode;
              newswitchsetting.enabled = CacheStorageAdapter.isOnline && CacheStorageAdapter.isDownloadComplete;
            });
          }

          const res = await this.resAsync;
          const newsetting = new ActionSetting({
            caption: res.get('RefreshNow'),
            sortOrder: 2,
            svgIconName: res.svgName('Refresh-Image'),
            enabled: CacheStorageAdapter.isOnline,
            action: async () => {
              if (considerOfflineMode && (await await this.persistenceService.cacheAdapter.hasOfflineChanges())) {
                saveAction(true);
              } else {
                refreshAction();
              }
            },
            beginGroupBehind: true,
          });
          newsettings.set(AppService.Setting_SaveChangesNow, newsetting);

          const handler = async () => {
            if (CacheStorageAdapter.isOnline) {
              if (await this.persistenceService.cacheAdapter.hasOfflineChanges()) {
                newsetting.caption = res.get('SaveChangesNow');
                newsetting.svgIconName = res.svgName('Save-Image');
              } else {
                newsetting.caption = res.get('RefreshNow');
                newsetting.svgIconName = res.svgName('Refresh-Image');
              }
              newsetting.enabled = true;
            } else {
              newsetting.enabled = false;
            }
          };

          CacheStorageAdapter.isDataChanged.subscribe(async () => await handler());
          CacheStorageAdapter.isOnlineChanged.subscribe(async () => await handler());
        }
      }
      config.settings = newsettings;

      const orderSetting = new ActionSetting({
        designation: AppService.Setting_Order,
        caption:
          this.workMode === WorkModeEnum.Ticket
            ? (await this.resAsync).get('Workmode-Ticket')
            : this.order?.designation,
        captionStyle: CaptionStyle.Bold,
        svgIconName: (await this.resAsync).svgName('ChangeOrder-Image'),
        action: () => this.basysMapFrameService.routing.navigateName(AppService.Route_WorkModeSelection),
        sortOrder: -1,
        beginGroupBehind: true,
      });
      orderSetting.enabled = this.basysMapFrameService.canChangeContext();
      CacheStorageAdapter.isDataChanged.subscribe(() => {
        orderSetting.enabled = this.basysMapFrameService.canChangeContext();
      });

      config.settings.set(AppService.Setting_Order, orderSetting);
      config.settings.set(
        AppService.Setting_ShowWorkDetails,
        new BooleanSetting({
          caption: (await this.resAsync).get('ShowWorkDetail'),
          value: ConvertHelper.toBoolean(localStorage.getItem(AppService.Setting_ShowWorkDetails) ?? 'false'),
          valueChanged: (val) => {
            localStorage.setItem(AppService.Setting_ShowWorkDetails, ConvertHelper.toString(val));
            this.basysMapFrameService.lib.closeBottomSheet();
          },
        }),
      );
      const p = await this.profileService.getCurrentProfile(true).toPromise();
      const profileSettings = p?.settings;
      if (profileSettings?.Commands?.length > 0) {
        const command = profileSettings.Commands.find(
          (c: any) => c.name === AppService.ContextMenuCommandName_Opera_ShowSequenceWorksList,
        );
        if (command) {
          config.settings.set(
            AppService.Setting_AggregatePathWorks,
            new BooleanSetting({
              caption: (await this.resAsync).get('AggregatePathWorks'),
              value: ConvertHelper.toBoolean(localStorage.getItem(AppService.Setting_AggregatePathWorks) ?? 'true'),
              valueChanged: (val) => {
                localStorage.setItem(AppService.Setting_AggregatePathWorks, ConvertHelper.toString(val));
                this.basysMapFrameService.lib.closeBottomSheet();
              },
            }),
          );
        }
      }
      config.settings.set(
        AppService.Setting_SaveChangesImmediately,
        new BooleanSetting({
          caption: (await this.resAsync).get('SaveChangesImmidiately'),
          value: ConvertHelper.toBoolean(localStorage.getItem(AppService.Setting_SaveChangesImmediately) ?? 'true'),
          valueChanged: (val) => {
            localStorage.setItem(AppService.Setting_SaveChangesImmediately, ConvertHelper.toString(val));
            this.basysMapFrameService.lib.closeBottomSheet();
          },
        }),
      );

      config.settings.set(
        AppService.Setting_ExportWorkData,
        new ActionSetting({
          caption: (await this.resAsync).get('ExportWorkData'),
          svgIconName: (await this.resAsync).svgName('Download-Image'),
          action: async () => await this.frameService.exportWorkData(this.order?.designation, false),
          groupName: (await this.resAsync).get('MenuGroupCaption-Export'),
        }),
      );

      if (considerOfflineMode) {
        const exportOfflineSetting = new ActionSetting({
          caption: (await this.resAsync).get('ExportOfflineWorkData'),
          svgIconName: (await this.resAsync).svgName('Download-Image'),
          action: async () => await this.frameService.exportWorkData(this.order?.designation, true),
          groupName: (await this.resAsync).get('MenuGroupCaption-Export'),
        });
        config.settings.set(AppService.Setting_ExportOfflineWorkData, exportOfflineSetting);
        const handler = async () => {
          if (await this.persistenceService.cacheAdapter.hasOfflineChanges()) {
            exportOfflineSetting.enabled = true;
          } else {
            exportOfflineSetting.enabled = false;
          }
        };

        CacheStorageAdapter.isDataChanged.subscribe(async () => await handler());
        CacheStorageAdapter.isOnlineChanged.subscribe(async () => await handler());
        await handler();
      }

      try {
        const pushNotificationsSetting = new BooleanSetting({
          caption: (await this.resAsync).get('EnablePushNotifications'),
          enabled: false,
          sortOrder: 4,
          value: undefined,
          valueChanged: async (newvalue) => {
            if (pushNotificationsSetting.enabled && this.pushAdapter) {
              if (newvalue) {
                await this.pushAdapter.activatePushNotifications();
              } else {
                await this.pushAdapter.deactivatePushNotifications();
              }
            }
          },
        });
        config.settings.set(AppService.Setting_EnablePushNotifications, pushNotificationsSetting);

        const pushAdapterSettings: PushAdapterSettings = new PushAdapterSettings();
        pushAdapterSettings.onActiveChanged = (active) => {
          pushNotificationsSetting.value = active;
          this.basysMapFrameService.detectChanges();
        };
        pushAdapterSettings.onActivatableChanged = (enabled) => {
          pushNotificationsSetting.enabled = enabled;
          this.basysMapFrameService.detectChanges();
        };
        pushAdapterSettings.onNotificationReceived = (data) => {
          if (!this.basysMapFrameService.hasLoggedOut) {
            //setTimeout only for res
            setTimeout(async () => {
              const res = await this.resAsync;
              let confirmationMessage: string = res.get('Push-Messages-NotificationHeader');
              const content: string = data.title + (data.body ? ' ' + data.body : null);
              if (content) {
                confirmationMessage += ':<br>' + content + '<br>';
              } else {
                confirmationMessage += '. ';
              }
              confirmationMessage += res.get('Push-Messages-ConfirmReloadData');
              this.showMessageBoxAndPerformAction(confirmationMessage, async () => {
                this.refreshData(true);
              });
            }, 100);

            data.icon = '/assets/resources/abc-maintenance-opera/appIcon.png';
            return data;
          }
        };
        pushAdapterSettings.onError = (type, err) => {
          //setTimeout only for res
          setTimeout(async () => {
            const res = await this.resAsync;
            switch (type) {
              case PushAdapterErrorType.UnableToSubscribeToNotificationService:
              case PushAdapterErrorType.UnableToSubscribeOnWebApiEndpoint:
                this.frameService.openSnackBar(res.get('Push-Messages-Error-UnableToSubscribe'), 2000);
                break;
              case PushAdapterErrorType.TimeoutWhileRequestingPermissions:
                this.frameService.openSnackBar(res.get('Push-Messages-Error-TimeoutWhileRequestingPermissions'), 2000);
                break;
            }
            console.error(PushAdapterErrorType[type]);
            if (err) {
              console.error(err);
            }
          }, 1);
        };

        pushAdapterSettings.getContextDefinition = async () => {
          return { orderid: await this.dataService.getOrderId() };
        };

        this.pushAdapter = await this.notificationsService?.tryCreatePushAdapter(
          this.basysMapFrameService.coreData,
          'opera',
          this.basysMapFrameService.cordovaService,
          'assets/service-workers/sw-opera-notifications-firebase.js',
          pushAdapterSettings,
        );
      } catch (err) {
        this.basysMapFrameService.error.publish(err);
      }

      config.contextMenuCommands.set(AppService.ContextMenuCommandName_Opera_ShowWorksList, {
        designation: AppService.ContextMenuCommandName_Opera_ShowWorksList,
        action: (entity) => this.frameService.showWorks([entity], WorkViewMode.AllFilteredByEntities),
        badgeValueExpressionParameters: [new ParameterValue('AuftragsId', this.order?.id)],
        enabledExpressionParameters: [new ParameterValue('AuftragsId', this.order?.id)],
        isHidden: () => !this.order,
      });
      config.contextMenuCommands.set(AppService.ContextMenuCommandName_Opera_ShowSequenceWorksList, {
        designation: AppService.ContextMenuCommandName_Opera_ShowSequenceWorksList,
        action: (startEntity) => {
          this.basysMapFrameService.processSequenceSelection(startEntity, true, (entities) =>
            this.frameService.processNetworkWorks(entities),
          );
        },
        isHidden: () => !this.order,
      });

      config.contextMenuCommands.set(AppService.ContextMenuCommandName_Opera_NewWork, {
        designation: AppService.ContextMenuCommandName_Opera_NewWork,
        action: (entity) => this.frameService.newWork([entity]),
        isHidden: () => !this.order,
      });

      const ticketLicensed: boolean = await this.dataService.isTicketLicensed();
      if (ticketLicensed) {
        config.contextMenuCommands.set(AppService.ContextMenuCommandName_Opera_ShowTicketsList, {
          designation: AppService.ContextMenuCommandName_Opera_ShowTicketsList,
          action: (entity) => this.frameService.showTickets([entity]),
          badgeValueExpressionParameters: [new ParameterValue('AuftragsId', this.order?.id)],
          enabledExpressionParameters: [new ParameterValue('AuftragsId', this.order?.id)],
        });
        config.contextMenuCommands.set(AppService.ContextMenuCommandName_Opera_NewTicket, {
          designation: AppService.ContextMenuCommandName_Opera_NewTicket,
          action: (entity) => this.frameService.newTicket([entity]),
          isOnlineOnly: true,
        });
      }

      // FIXME hasBackdrop = true => Issue with arena overlay
      config.hasBackdrop = false;
      config.showImageSettingsTab = true;
      config.bottomSheetHeightPercentage = 100;
      config.helpUrl = 'http://help.barthauer.de/BaSYS/9.26.1/Web/opera';
      // TODO Get IsReadOnly from profile
      //config.isObjectInfoReadOnly = false;

      config.additionalObjectTypeConfigs = [
        new AdditionalObjectTypeConfig({
          objectTypeName: 'MtWork',
          oPathFilter: 'AuftragsId In ($IdList)',
          filterParameters: [idListParam, auftragsIdParam],
          additionalColumns: [
            this.basysMapFrameService.data.createQueryColumn('Object.GeObjectId', '_navObjectId'),
            this.basysMapFrameService.data.createQueryColumn('Object.GeObjectType', '_navObjectType'),
          ],
          navigateObjectIdColumnName: '_navObjectId',
          navigateObjectTypeColumnName: '_navObjectType',
        }),
        new AdditionalObjectTypeConfig({
          objectTypeName: 'MtResult',
          oPathFilter: '!.!.AuftragsId In ($IdList)',
          filterParameters: [idListParam, auftragsIdParam],
          additionalColumns: [
            this.basysMapFrameService.data.createQueryColumn('!.!.Object.GeObjectId', '_navObjectId'),
            this.basysMapFrameService.data.createQueryColumn('!.!.Object.GeObjectType', '_navObjectType'),
          ],
          navigateObjectIdColumnName: '_navObjectId',
          navigateObjectTypeColumnName: '_navObjectType',
        }),
      ];

      return config;
    };
  }
  private getDataEntity(data: any): DataEntity {
    try {
      const jsonObj = JSON.parse(data);
      if (jsonObj?.id !== undefined && jsonObj?.objectType !== undefined) {
        const objType: number | string = jsonObj.objectType;
        const id: string = jsonObj.id;
        const entity: DataEntity = new DataEntity(objType, id);
        return entity;
      }
    } catch (err) {
      console.log(err);
      return undefined;
    }
  }

  public async ticketCommandsAvailable(): Promise<boolean> {
    let retSelected: boolean = false;
    const p = await this.profileService.getCurrentProfile(true).toPromise();
    const profileSettings = p?.settings;

    const ALWAYS_HIDDEN = 'AlwaysHidden';
    if (profileSettings?.Commands?.length > 0) {
      const command = profileSettings?.Commands?.find(
        (_) =>
          (_.name === AppService.ContextMenuCommandName_Opera_ShowTicketsList && _.visibility !== ALWAYS_HIDDEN) ||
          (_.name === AppService.ContextMenuCommandName_Opera_NewTicket && _.visibility !== ALWAYS_HIDDEN),
      );

      if (command) {
        retSelected = true;
      }
    }

    return retSelected;
  }

  private handleMapInfoLoaded(): void {
    this.basysMapFrameService.mapInfoLoaded = (mapInfo: MapInfoViewModel) => {
      this.dataService.isReadOnly = mapInfo.isReadOnly;
      if (mapInfo.isReadOnly) {
        const setting: BooleanSetting = this.basysMapFrameService.frameConfig.settings.get(
          AppService.Setting_SaveChangesImmediately,
        ) as BooleanSetting;
        setting.value = false;
        setting.enabled = false;
      }
      if (this.pushAdapter) {
        this.pushAdapter.checkActive();
      }
    };
  }

  private handleWidgetInstantiated(): void {
    this.basysMapFrameService.widgetInstantiated = (widgetConfig: WidgetConfiguration) => {
      if (widgetConfig.designation === AppService.Widget_Works) {
        this.frameService.sendMobileWorks();
      } else if (widgetConfig.designation === AppService.Widget_Tickets) {
        this.frameService.sendMobileTickets();
      } else if (widgetConfig.designation === AppService.Widget_Resources) {
        this.frameService.sendMobileResourceTypes();
      }
    };
  }

  private handleMapActions(): void {
    this.basysMapFrameService.map.onMapAction().subscribe((mapActionArgument) => {
      if (mapActionArgument.actionId === MapActionIds.CREATE_TICKET) {
        const entity = new DataEntity('MtTicket', null, mapActionArgument.feature);
        this.frameService.newTicket([entity]);
      }
    });
  }

  private getIncompleteWorksCount(): number {
    let ret: number = 0;
    const works = this.frameService.workViewPackage?.allWorks;
    if (works) {
      ret = works.filter((x) => x.state !== WorkState.Erledigt).length;
    }

    return ret;
  }

  private getIncompleteTicketsCount(): number {
    let ret: number = 0;
    const tickets = this.frameService.ticketViewPackage?.allTickets;
    if (tickets) {
      ret = tickets.filter((x) => x.state !== TicketState.Erledigt).length;
    }

    return ret;
  }

  public refreshWorksBadgeOnBottombar(): void {
    this.basysMapFrameService.refreshWidgets();
  }

  init(isStandalone?: boolean, operaPath?: string, operaOutlet?: string, preselectedDataEntities?: DataEntity[]) {
    const basysMapFrameInitConfig = new BasysMapFrameInitConfig();
    basysMapFrameInitConfig.isStandalone = isStandalone;
    basysMapFrameInitConfig.mapsPath = operaPath;
    basysMapFrameInitConfig.mapsOutlet = operaOutlet;
    basysMapFrameInitConfig.preselectedDataEntities = preselectedDataEntities;
    this.basysMapFrameService.init(basysMapFrameInitConfig).finally(() => {});
  }

  armMobileOrdersQuery() {
    this.queryMobileOrdersForSelection$ = this.queryMobileOrders();
  }
  armMobileOrderQuery(order: MobileOrderViewModel) {
    this.queryMobileOrders(order.dbObject.Id).subscribe(async (order) => {
      await this.loadOrderAsync(order[0]);
      if (!this.orders) this.orders = [];
      if (!this.orders.find((mobileOrder) => mobileOrder.id === order[0].id)) this.orders.push(order[0]);
      this.queryMobileOrdersForSelection$ = of(this.orders);
      this.basysMapFrameService.routing.navigate(this.basysMapFrameService.rootRoute);
    });
  }

  ensureHasOrderOrTickets(): Observable<boolean> {
    if (this.order) {
      return of(true);
    } else {
      return this.queryMobileOrders().pipe(
        concatMap(async (orders) => {
          this.orders = orders;
          const lastOrderId = await this.getLastOrderIdAsync();
          const ticketOnlyMode: boolean = this.checkIfLastWorkModeTicketOnly(lastOrderId);
          this.workMode = ticketOnlyMode ? WorkModeEnum.Ticket : WorkModeEnum.Default;

          const hasTickets: boolean = await this.dataService.hasMtTickets().toPromise();
          const index = orders.findIndex((_) => _.id == lastOrderId);

          if (ticketOnlyMode) {
            this.loadTicketsAsync();
            return true;
          } else {
            if (index >= 0) {
              await this.loadOrderAsync(orders[index]);
              return true;
            } else if (orders.length === 1 && !hasTickets) {
              await this.loadOrderAsync(orders[0]);
              return true;
            } else if (orders.length === 1 && hasTickets) {
              this.queryMobileOrdersForSelection$ = of(orders);
              this.basysMapFrameService.routing.navigate(this.frameService.orderSelectionRoute);
              return true;
            } else if (orders.length === 0 || orders.length > 1) {
              this.queryMobileOrdersForSelection$ = of(orders);
              this.basysMapFrameService.routing.navigate(this.frameService.orderSelectionRoute);
              return orders.length === 0;
            }
          }
        }),
      );
    }
  }
  checkIfLastWorkModeTicketOnly(lastOrderId: string): boolean {
    let ret: boolean = false;
    if (lastOrderId) ret = lastOrderId === AppService.WORKMODE_TICKETONLY_ID;
    return ret;
  }

  queryMobileOrders(id?: string): Observable<MobileOrderViewModel[]> {
    return this.dataService.getOrders(id).pipe(
      concatMap(async (data) => {
        const orders: MobileOrderViewModel[] = [];
        for (const order of data) {
          const isDownloaded = await this.isOrderDownloadedAsync(order.Id);
          let add = true;
          if (CacheStorageAdapter.isDownloadComplete) {
            add = isDownloaded;
            if (!add && !CacheStorageAdapter.forceOfflineMode) {
              add = CacheStorageAdapter.isOnline;
            }
          }
          if (add) {
            orders.push(new MobileOrderViewModel(order, isDownloaded));
          }
        }
        if (id) {
          return orders;
        } else {
          return orders.sort((a, b) => b.date.getTime() - a.date.getTime());
        }
      }),
      catchError((err) => {
        this.basysMapFrameService.ensureServerAvailable();
        return throwError(err);
      }),
    );
  }

  private async isOrderDownloadedAsync(id: string): Promise<boolean> {
    if (!id) {
      return false;
    }
    const settings = await this.profileSettingsService.getCurrentProfileSettingsWithContext(id).toPromise();
    const contextKey = await this.profileSettingsService.buildProfileKey(settings).toPromise();
    return (
      (await this.basysMapFrameService.cache.isDataSetAvailableForContext(
        CachePersistenceAdapter.CacheDataSetName,
        contextKey,
        CachePersistenceAdapter.StateTableName,
        this.dataService.lastStateContext,
      )) && (await this.basysMapFrameService.cache.isDataSetAvailableForContext('Arena', contextKey)) //HACK: Find better solution instead of 'Arena'
    );
  }

  public async isTicketsDownloadedAsync(): Promise<boolean> {
    return await this.isOrderDownloadedAsync(AppService.WORKMODE_TICKETONLY_ID);
  }

  public async loadOrderAsync(order: MobileOrderViewModel): Promise<void> {
    this.workMode = WorkModeEnum.Default;
    await this.setLastOrderIdAsync(order.id);
    this.loadOrderSubject.next(order);
  }

  public async loadTicketsAsync(): Promise<void> {
    this.workMode = WorkModeEnum.Ticket;
    await this.setLastOrderIdAsync(AppService.WORKMODE_TICKETONLY_ID);
    this.loadOrderSubject.next(null);
  }

  public callDisplayLoader(value) {
    this.displayLoaderSubject.next(value);
  }

  public dispose(): void {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  public async getLastOrderIdAsync(): Promise<string> {
    return await this.profileSettingsService
      .getCurrentProfileSettings()
      .pipe(map((_) => _?.contextIdentifier))
      .toPromise();
  }

  public async setLastOrderIdAsync(value: string): Promise<void> {
    return await this.profileSettingsService
      .getCurrentProfileSettingsWithContext(value)
      .pipe(
        concatMap((settings) => {
          return this.profileSettingsService.updateCurrentProfileSettings(settings);
        }),
      )
      .toPromise();
  }

  public async showMessageBoxAndPerformAction(
    message: string,
    action: () => Promise<void>,
    closeExisting: boolean = true,
  ) {
    const logic = async () => {
      const messageSettings = new MessageSettings();
      messageSettings.mode = MessageMode.Question;
      messageSettings.icon = MessageBoxIcon.Question;
      messageSettings.button = MessageBoxButtons.YesNo;
      messageSettings.htmlcode = document.createElement('div');
      messageSettings.htmlcode.innerHTML = message;

      const result = await this.messageBoxService.showMessageBox(messageSettings).toPromise();
      if (result && result['result'] === DialogResult.Yes) {
        await action();
      }
    };

    if (closeExisting) {
      this.messageBoxService.closeAllBoxes();
      setTimeout(() => {
        logic();
      }, 100);
    } else {
      logic();
    }
  }

  public static async getWorkTypeFilterCore(userService: UserService, additionalFilter?: string): Promise<string> {
    return await DataService.getWorkTypeFilterCore(userService, additionalFilter);
  }
}
